// ============================================================================
// src/shared/Header.tsx  —  Phase 2 Round 6 tail + Notifications Center prep
// ----------------------------------------------------------------------------
// The top nav bar + program sub-nav. Reads view/menu state from UIContext,
// auth/save-status from AuthContext, form open-state booleans from
// BatchesContext + PigContext. A handful of App-only things come as props:
// B2.2c: Header takes NO props. It reads auth, UI, batch and pig state from
// React element (depends on App's deleteConfirm state).
//
// 2026-05-14 navigation prep for the future Notifications Center:
//   - Webforms group (Dailys + Equipment text buttons) moved out of the
//     dark bar into the hamburger menu. The menu is the single broader-
//     navigation surface for every logged-in role.
//   - Tasks converted from a text button to an icon-only white circle on
//     the green bar; existing count badge and click behavior preserved.
//   - Notifications placeholder icon button added next to Tasks. NO fake
//     data, NO badge until the storage lane lands — this slot reserves
//     header layout so the Notifications Center can drop in without
//     another header refactor.
//   - Sign Out moved into the hamburger; the only top-bar exit point now
//     is via the menu. Keeps the header light at mobile widths.
//
// App() wraps this in a local `Header` factory closure so every extracted
// view can keep receiving `Header` as a zero-arg prop — no ripple changes
// across the ~50 call sites.
// ============================================================================
import React from 'react';
import type {CSSProperties, ReactNode} from 'react';
import {createPortal} from 'react-dom';
import {useAppGuardedNavigation, useAppLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {S, getReadableText} from '../lib/styles.js';
import {getProgramColor} from '../lib/programColors.js';
import {PROFILE_STATUS, useAuth} from '../contexts/AuthContext.jsx';
import type {AuthIdentity} from '../contexts/AuthContext.jsx';
import {useUI} from '../contexts/UIContext.jsx';
import {resolveNotificationRoute, routeToView} from '../lib/activityRegistry.js';
import type {ActivityNotification} from '../lib/activityRegistry.js';
import {navigateToProcessingRoute} from '../lib/processingNav.js';
import {readProcessingDeepLink} from '../processing/processingDrawerLocation.js';
import {useBatches} from '../contexts/BatchesContext.jsx';
import {usePig} from '../contexts/PigContext.jsx';
import {markNotificationRead, markAllNotificationsRead} from '../lib/notificationsApi.js';
import {loadNotificationTarget, readMentionLinkId} from '../lib/mentionEmailApi.js';
import {deriveNavSelection, filterGlobalDestinations, isProcessingRole} from '../lib/globalNav.js';
import {
  canAccessDailyReports,
  canAccessProgram,
  canLightAccessView,
  DAILY_REPORT_VIEWS,
  isInactiveAccount,
} from '../lib/access.js';
import type {AuthSession} from '../lib/access.js';
import {sb} from '../lib/supabase.js';
import {useHeaderShellResources} from '../app/shell/useHeaderShellResources.js';
import type {BottomTab} from '../lib/bottomBar.js';
import {bottomBarCanOpen, deriveHeaderBottomBar, type HeaderAuth} from '../lib/headerBottomBar.js';
import {useOpenOverlayCount} from '../lib/openOverlays.js';
import {mergeSaveStatus, useSaveStatus, type LegacySaveStatus} from '../lib/saveStatus.js';
import {usePageBottomBarConfig} from '../lib/pageBottomBar.js';
import {usePhoneLayout} from './usePhoneLayout.js';
import {scrollActiveIntoStrip} from './navStripScroll.js';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import InlineNotice from './InlineNotice.jsx';
import type {Notice} from './InlineNotice.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import PlannerIcon from '../components/PlannerIcon.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import GlobalNavStrip from './GlobalNavStrip.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import HeaderWeatherChip from './HeaderWeatherChip.tsx';
import BottomBar from './BottomBar.jsx';
import {ProgramSwitcher, SaveStatusChipView, type ProgramChoice} from './HeaderPieces.jsx';

// Notifications Center: enabled now that mig 057 ships the notifications
// table and complete_task_instance v2 inserts a 'task_completed'
// notification for the task creator when somebody else completes it.
// The bell button renders a dropdown panel; the badge shows the
// recipient's unread count from real DB data (no fakes).
const NOTIFICATIONS_CENTER_ENABLED = true;

// In-flight / settled state for emailed mention links, keyed by
// `${subjectId}:${notificationId}`.
//
// This deliberately replaces an earlier permanent "already attempted" Set. The
// Header is mounted by App AND by several program views, so some shared state is
// needed to stop one link being resolved once per mount — but a claim that is
// never released turns any single failed attempt into a permanent dead end for
// the rest of the page's life, which is exactly how the emailed link came to
// land on the home page without a word. `settled` is only ever set for outcomes
// that must not be repeated; a failed request leaves the entry retryable.
const MENTION_LINK_MAX_ATTEMPTS = 3;
const MENTION_LINK_RETRY_MS = 1200;
type MentionLinkState = {attempts: number; inFlight: boolean; settled: boolean};
const mentionLinkState = new Map<string, MentionLinkState>();

// The notification columns the dropdown actually renders, and no more.
// The Header shell resource returns `unknown[]` ON PURPOSE — its own doc
// comment says so: the RPC and the fallback SELECT return the columns as SQL
// defines them, this repository has no generated database types, and "the
// Header narrows what it renders itself". This is that narrowing, stated once
// where the columns are known, exactly as BroilerListView narrows the
// `unknown[]` rows its context hands it.
//
// It EXTENDS ActivityNotification rather than restating the four fields
// resolveNotificationRoute branches on, so a row can be handed to that
// function without a second shape claim, and so a change there cannot silently
// diverge from this.
type HeaderNotificationRow = ActivityNotification & {
  id: string;
  title?: string | null;
  body?: string | null;
  read_at?: string | null;
  created_at: string;
};

// Homepage-redesign header treatment (modern-base.css .icon-btn + .header-green):
// translucent white glyphs sitting directly on the green bar — NOT white-filled
// boxes. 36×36 hit target, 10px radius. Active = a soft white wash. Hover wash
// is added via a global rule in index.html ([data-header-action-group] button).
const HEADER_ICON_BTN: CSSProperties = {
  width: 36,
  height: 36,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  padding: 0,
  borderRadius: 10,
  border: 'none',
  background: 'transparent',
  color: 'rgba(255,255,255,.96)',
  cursor: 'pointer',
  fontSize: 16,
  lineHeight: 1,
  fontFamily: 'inherit',
  transition: 'background .15s',
};

const HEADER_ICON_BTN_ACTIVE: CSSProperties = {
  ...HEADER_ICON_BTN,
  background: 'rgba(255,255,255,.18)',
};

// Red badge for unread/open counts on header icons. White border so the
// badge pops cleanly off the white button.
const HEADER_BADGE: CSSProperties = {
  position: 'absolute',
  top: -4,
  right: -4,
  padding: '0 5px',
  minWidth: 16,
  height: 16,
  lineHeight: '16px',
  fontSize: 10,
  fontWeight: 700,
  borderRadius: 999,
  background: '#dc2626',
  color: 'white',
  textAlign: 'center',
  border: '1px solid #ffffff',
};

// Homepage-redesign user chip (modern-base.css .user/.avatar/.user-txt):
// a rounded pill on the green bar holding a circular avatar (initial) + the
// name/role stacked. Replaces the old plain-text "Name · role".
const USER_PILL: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  padding: '3px 12px 3px 4px',
  borderRadius: 999,
  background: 'rgba(255,255,255,.16)',
};
const USER_AVATAR: CSSProperties = {
  width: 26,
  height: 26,
  flexShrink: 0,
  borderRadius: '50%',
  background: 'rgba(255,255,255,.92)',
  color: '#0b3530',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontWeight: 700,
  fontSize: 12,
};
const USER_TXT: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  lineHeight: 1.05,
  fontSize: 13,
  fontWeight: 650,
  color: '#ffffff',
};

// Inline monochrome SVG icons that use currentColor so the parent button's
// `color` style controls the stroke. Avoids the "mixed white-on-colored-
// tile" anti-pattern of dropping a colored PNG inside a white button.
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
function CheckmarkIcon({size = 18}: {size?: number}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M5 12l5 5L20 7" />
    </svg>
  );
}

// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
function BellIcon({size = 18}: {size?: number}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M18 8a6 6 0 1 0-12 0c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </svg>
  );
}

// Menu / navigation glyphs. The design handoff ships Unicode + emoji as
// stand-ins only ("Emoji removed. Replace with the Planner's existing icon set
// — inline stroke SVGs using currentColor"). These replace the pre-lane ☰ and
// the seven emoji that labelled the hamburger destinations; where the Planner
// already owns a raster icon for a destination (Dailys, Equipment, Processing,
// Admin) the menu keeps PlannerIcon instead of duplicating artwork here.
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
function GlyphSvg({size = 16, children}: {size?: number; children?: ReactNode}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {children}
    </svg>
  );
}

// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
function MenuIcon({size = 18}: {size?: number}) {
  return (
    <GlyphSvg size={size}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </GlyphSvg>
  );
}

// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
function HomeGlyph({size = 16}: {size?: number}) {
  return (
    <GlyphSvg size={size}>
      <path d="M4 10.5 12 4l8 6.5" />
      <path d="M6 10v9h12v-9" />
    </GlyphSvg>
  );
}

// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
function ActivityGlyph({size = 16}: {size?: number}) {
  return (
    <GlyphSvg size={size}>
      <path d="M20 14a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2z" />
    </GlyphSvg>
  );
}

// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
function UsersGlyph({size = 16}: {size?: number}) {
  return (
    <GlyphSvg size={size}>
      <path d="M16 19v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 17.5V19" />
      <circle cx="10" cy="8" r="3.2" />
      <path d="M20 19v-1.5a3.5 3.5 0 0 0-2.6-3.38" />
      <path d="M15.5 5.2a3.2 3.2 0 0 1 0 5.6" />
    </GlyphSvg>
  );
}

// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
function NewsletterGlyph({size = 16}: {size?: number}) {
  return (
    <GlyphSvg size={size}>
      <path d="M4 6.5h12v13H5.5A1.5 1.5 0 0 1 4 18z" />
      <path d="M16 9.5h2.5A1.5 1.5 0 0 1 20 11v7a1.5 1.5 0 0 1-1.5 1.5H16" />
      <path d="M7 10h6M7 13.5h6M7 17h4" />
    </GlyphSvg>
  );
}

// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
function SignOutGlyph({size = 16}: {size?: number}) {
  return (
    <GlyphSvg size={size}>
      <path d="M14 5h4.5A1.5 1.5 0 0 1 20 6.5v11a1.5 1.5 0 0 1-1.5 1.5H14" />
      <path d="M10 8l-4 4 4 4" />
      <path d="M6 12h9" />
    </GlyphSvg>
  );
}

// Hamburger menu item shape. Generic; section labels override `style`.
const MENU_ITEM_BTN: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  width: '100%',
  padding: '10px 16px',
  border: 'none',
  background: 'none',
  cursor: 'pointer',
  fontSize: 13,
  textAlign: 'left',
  color: 'var(--text-primary)',
  fontFamily: 'inherit',
};

const MENU_SECTION_LABEL: CSSProperties = {
  display: 'block',
  padding: '8px 16px 4px',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: '#68707A',
};

const MENU_DIVIDER: CSSProperties = {
  height: 1,
  background: '#F1F3F4',
  margin: '4px 0',
};

// Header stacking contract: the sticky header bar (position:sticky + z-index)
// establishes a stacking context, so its absolutely-positioned dropdowns are
// trapped at the bar's level — below page content like the Pasture map, whose
// chrome stacks at z-index 600-2000. Portaling the dropdowns to <body> escapes
// that trap; this z-index sits above all page content but below the app's
// blocking modal tier (notifications/users/confirm modals at 9100-11000), so
// real modals still cover the header. Applies app-wide, not just to Pasture.
const HEADER_OVERLAY_Z = 9000;

// Fixed-position style for a portaled dropdown, anchored under the right edge of
// its toggle button. Falls back to a sane top-right spot before the ref mounts.
function headerAnchorStyle(anchorRef: {current: HTMLElement | null} | null): CSSProperties {
  const rect = anchorRef && anchorRef.current && anchorRef.current.getBoundingClientRect();
  if (!rect || typeof window === 'undefined') return {position: 'fixed', top: 56, right: 12};
  return {position: 'fixed', top: Math.round(rect.bottom + 6), right: Math.round(window.innerWidth - rect.right)};
}

const ROLE_PREVIEW_LABELS: Record<string, string> = {
  admin: 'Admin',
  management: 'Management',
  farm_team: 'Farm Team',
  equipment_tech: 'Equipment Tech',
  light: 'Light',
  inactive: 'Inactive',
};

const ROLE_PREVIEW_SELECT: CSSProperties = {
  width: '100%',
  padding: '7px 9px',
  borderRadius: 10,
  border: '1px solid #D2D6DB',
  background: 'white',
  color: 'var(--text-primary)',
  fontSize: 12,
  fontFamily: 'inherit',
};

// NOTE: the pre-lane brand "section pill" (BROILERS / CATTLE / SHEEP next to
// the wordmark) is gone. The global program strip now names the active program
// with a selected white chip at every breakpoint, so the pill was duplicating
// that state while spending header width — the exact reason it was already
// hidden on mobile.

export default function Header() {
  // AuthState's third member is the signed-out `false`, and optional chaining
  // does NOT remove it — `authState?.role` on `false` evaluates `false.role`
  // and yields undefined at runtime, exactly as it does for null. This is the
  // same narrowing CattleHerdsView.tsx and BroilerListView.tsx already apply to
  // the identical reads: it lets the checker see the property accesses the
  // shipped code has always made, and it erases at build time, so every read
  // below stays byte-for-byte what it was.
  const {
    authState,
    realAuthState,
    signOut,
    saveStatus,
    rolePreview,
    setRolePreview,
    clearRolePreview,
    rolePreviewRoles,
    canUseRolePreview,
    sessionEpoch,
    profileStatus,
  } = useAuth() as Omit<ReturnType<typeof useAuth>, 'authState' | 'realAuthState'> & {
    authState: AuthIdentity | null;
    realAuthState: AuthIdentity | null;
  };
  const {view, setView, showMenu, setShowMenu} = useUI();
  // B2.2c: the predicates are module functions in lib/globalNav.js now.
  // Header binds the program one to the authState it already reads,
  // instead of being handed App's closure copy as a prop.
  // AuthContext types `profile` as `unknown` on purpose (its columns live in
  // SQL). access.ts describes the same row by the ONE column it reads,
  // program_access. Naming that boundary here is the repo's existing idiom for
  // profile reads (see PigDailysView.tsx) and erases at build time.
  // Migration 218: a deactivated account (positively loaded profile, role
  // 'inactive') gets the bare bar -- brand and identity only. No program chips,
  // no section strip, no Tasks / Notifications / Weather / menu, and no Header
  // read starts (headerShellScopeOrNull refuses it; the mention-link lookup
  // below is gated too). Sign Out lives on InactiveAccountScreen's card.
  const inactiveAccount = isInactiveAccount(authState as AuthSession | null);
  // The route layer tolerates a missing profile while booting. Navigation must
  // wait for a verified profile rather than showing tabs over an empty shell.
  const navigationReady =
    profileStatus === PROFILE_STATUS.RESOLVED && !!authState?.user && !!authState?.profile && !inactiveAccount;
  const canAccessProgramHere = (prog: string | null) =>
    navigationReady && canAccessProgram(authState as AuthSession | null, prog);
  // Use the same surface and destination checks as the phone navigation for
  // every header link, including the desktop tabs and hamburger entries.
  const permittedHeaderView = bottomBarCanOpen(authState as HeaderAuth);
  const canOpenHeaderView = (target: string) => navigationReady && permittedHeaderView(target);
  // Daily-report carve-out: a farm_team user reaches every program's daily
  // surfaces regardless of program_access, so the section strip must offer those
  // tabs even for a program they do not hold. The eligible list is passed in
  // rather than decided in globalNav, which states no role rule of its own.
  // Cattle Log rides along: resolveSurfaceAccess exempts it for the same roles,
  // so omitting it would leave a farm_team user on /cattle/log looking at a
  // one-tab strip with nothing active.
  const dailyReportSectionViews = canAccessDailyReports(authState as AuthSession | null)
    ? [...DAILY_REPORT_VIEWS, 'cattlelog']
    : null;
  const headerNavigate = useNavigate();
  const requestGuardedNavigation = useAppGuardedNavigation();
  const headerLocation = useAppLocation();
  const {showForm, setShowForm} = useBatches();
  const {showBreedForm, setShowBreedForm, showFarrowForm, setShowFarrowForm} = usePig();
  const callerProfileId = authState && authState.user ? authState.user.id : null;
  // The globally mounted Header owns its chrome, not a route surface's data.
  // Its task and notification reads therefore live in the dedicated scoped
  // shell registry, wait for a positive boot epoch, and settle independently.
  const {
    taskBadgeCount: myDueCount,
    notificationUnread: notifUnread,
    notificationRecent: rawNotifRecent,
    notificationLoading: notifLoading,
    notificationFailed,
    retryNotifications,
  } = useHeaderShellResources({authState, rolePreview, sessionEpoch, view});
  const notifRecent = rawNotifRecent as HeaderNotificationRow[];
  const notifLoadError: Notice | null = notificationFailed
    ? {kind: 'error', message: 'Notifications failed to load. Retry when your connection is back.'}
    : null;
  // Header still owns only the dropdown's open/closed overlay state.
  const [notifOpen, setNotifOpen] = React.useState(false);

  // ── Emailed mention links ────────────────────────────────────────────
  // A mention email links to /?n=<notification id>, not to a route of its
  // own: the home path is already released, so a signed-out click still
  // lands somewhere real and the parameter survives the login round-trip.
  //
  // The destination is resolved HERE, by resolveNotificationRoute — the same
  // owner the bell below uses. That is the whole point of the opaque link:
  // the email never carries a record path, so there is no second route table
  // to keep in step, and a forwarded link is inert because
  // get_notification_target returns nothing to anyone but the recipient.
  // Latest identity/epoch, so a response that lands after a sign-out or an
  // account switch can be recognised as stale instead of acted on.
  const mentionLinkSubjectRef = React.useRef<string | null>(callerProfileId);
  mentionLinkSubjectRef.current = callerProfileId;
  const mentionLinkEpochRef = React.useRef<number>(sessionEpoch);
  mentionLinkEpochRef.current = sessionEpoch;
  const [mentionLinkRetryTick, setMentionLinkRetryTick] = React.useState(0);
  const [mentionLinkFailed, setMentionLinkFailed] = React.useState(false);

  React.useEffect(() => {
    // Wait for the SAME positive session-readiness boundary the header's other
    // authenticated reads use. callerProfileId alone is not that boundary: it is
    // set as soon as an identity exists, which can be before the session can
    // actually authorise a PostgREST call. Firing the lookup early produced an
    // error that was swallowed, and the attempt was never repeated.
    if (!callerProfileId) return;
    if (inactiveAccount) return;
    if (typeof sessionEpoch !== 'number' || !Number.isSafeInteger(sessionEpoch) || sessionEpoch <= 0) return;

    const linkId = readMentionLinkId();
    if (!linkId) return;

    const key = `${callerProfileId}:${linkId}`;
    const state = mentionLinkState.get(key) ?? {attempts: 0, inFlight: false, settled: false};
    if (state.settled || state.inFlight || state.attempts >= MENTION_LINK_MAX_ATTEMPTS) return;
    state.inFlight = true;
    state.attempts += 1;
    mentionLinkState.set(key, state);

    const subjectAtStart = callerProfileId;
    const epochAtStart = sessionEpoch;

    void (async () => {
      const result = await loadNotificationTarget(sb, linkId);
      state.inFlight = false;

      // Stale: the session changed under us, or the operator has already moved
      // somewhere else. Either way this response must not steer the app.
      const stale =
        mentionLinkSubjectRef.current !== subjectAtStart ||
        mentionLinkEpochRef.current !== epochAtStart ||
        readMentionLinkId() !== linkId;
      if (stale) {
        state.settled = true;
        return;
      }

      if (!result.ok) {
        // Retryable. Leave `settled` false so a later pass can try again, and
        // only give up — visibly — once the attempt budget is spent.
        if (state.attempts >= MENTION_LINK_MAX_ATTEMPTS) {
          setMentionLinkFailed(true);
          return;
        }
        window.setTimeout(() => setMentionLinkRetryTick((n) => n + 1), MENTION_LINK_RETRY_MS);
        return;
      }

      // A successful lookup is final either way: a notification that is not this
      // caller's must stay non-disclosing and must not be retried.
      state.settled = true;
      setMentionLinkFailed(false);
      const target = result.target;
      if (!target) return;

      const route = resolveNotificationRoute(target, target.activity_entity_type, target.activity_entity_id);
      if (!route.startsWith('/')) return;

      // Opening the record must not depend on the mark-read write. That write is
      // fire-and-forget: a slow or failing badge update is not a reason to leave
      // the operator on the home page.
      if (!target.read_at) {
        void Promise.resolve(markNotificationRead(sb, linkId)).catch(() => {
          /* badge only */
        });
      }

      // Same processing carve-out as the bell: /processing?record=<id> has to
      // keep its query string, which the view-to-URL adapter would drop.
      if (route.startsWith('/processing')) {
        navigateToProcessingRoute(headerNavigate, route);
        return;
      }
      headerNavigate(route);
    })();
  }, [callerProfileId, inactiveAccount, sessionEpoch, headerNavigate, mentionLinkRetryTick]);
  // Ref on the section sub-nav so an effect can scroll the active tab into
  // view after a section/view change. Without this, the operator could land
  // on a route whose tab sits off-screen to the right (cattle has 8 tabs;
  // pigs has 8) and not realize the active one wasn't the first visible.
  const subnavRef = React.useRef<HTMLDivElement | null>(null);
  // Toggle-button anchors for the portaled header overlays (see HEADER_OVERLAY_Z).
  const menuBtnRef = React.useRef<HTMLButtonElement | null>(null);
  const notifBtnRef = React.useRef<HTMLButtonElement | null>(null);
  // Light users may reach light-allowed cattle/daily views (cattledailys,
  // cattlelog, broilerdailys, ...) but must never see any program module's tab
  // strip: every other tab in that strip is a surface Light is bounced out of.
  // deriveNavSelection() enforces that for ALL programs (pre-lane the carve-out
  // covered cattle only, so Light on /broiler/dailys was shown five dead tabs).
  const isLight = authState?.role === 'light';
  // Owner answer E: the menu offers Dailys only where access.ts lets the caller in.
  const offersDailys = canOpenHeaderView('webformhub');
  const isAdmin = authState?.role === 'admin';

  // ── Route-derived navigation state ──
  // Nothing below is stored. The current program, its section set, and the
  // active section are all derived from `view` (which App's URL adapter
  // owns) via the shared config in src/lib/globalNav.js. There is no
  // currentProgram/currentSection state to drift out of sync with the URL.
  const {
    navKey,
    program: navProgram,
    sections: programSections,
    activeSectionView,
  } = deriveNavSelection({
    view,
    showForm,
    showBreedForm,
    showFarrowForm,
    isLight,
    canAccessProgram: canAccessProgramHere,
    dailyReportSectionViews,
  });
  const sections = programSections.filter((section) => canOpenHeaderView(section.view));

  // The nine bar destinations, filtered to what this user may actually reach.
  // Every decision delegates to App's own predicates — this never restates a
  // permission rule (see filterGlobalDestinations).
  //
  // Deliberately NOT memoized: canAccessProgramHere closes over authState and
  // is rebuilt every render, so a useMemo keyed on it would recompute anyway
  // while reading as though it did not. Filtering nine static entries is
  // cheaper than the pretence.
  const globalDestinations = !navigationReady
    ? []
    : filterGlobalDestinations({
        isLight,
        isAdmin,
        role: authState?.role,
        canAccessProgram: canAccessProgramHere,
        canLightAccessView,
      }).filter((destination) => canOpenHeaderView(destination.view));
  // Only light a chip the user can actually see. A Light user on /cattle/log
  // derives navProgram 'cattle', but the cattle chip is filtered out, so the
  // strip must report no selection rather than a phantom one.
  const activeNavKey = globalDestinations.some((d) => d.key === navKey) ? navKey : null;

  const inSubnav = navigationReady && sections.length > 0;

  // ── One save indicator (F02-B, Q02) ──
  // App-store blob saves still report through AuthContext's saveStatus; every
  // other autosave reports to src/lib/saveStatus. The header shows them as one
  // chip. data-header-save-status keeps its three values for the specs that
  // wait on it ('saving' / 'saved' / 'error').
  const saveSnapshot = mergeSaveStatus(useSaveStatus(), (saveStatus || '') as LegacySaveStatus);
  const hasSaveStatus = saveSnapshot.state !== 'idle';
  const saveHook = saveSnapshot.state === 'failed' ? 'error' : saveSnapshot.state;

  // ── Phone program switcher (Q18) and bottom bar (N02, Q09, Table C) ──
  // Both read the same derived selection as the strips above; neither states
  // an access rule. The bar asks the one surface-access owner whether each
  // destination may open; a page with its own modes (Pasture) registers them
  // in src/lib/pageBottomBar instead of passing props (Header stays zero-prop).
  const switcherChoices: ProgramChoice[] = [
    {key: 'home', label: 'Home', view: 'home'},
    ...globalDestinations.map((d) => ({key: d.key, label: d.label, view: d.view})),
  ];
  const switcherCurrent =
    switcherChoices.find((c) => c.key === activeNavKey) ?? (view === 'home' ? switcherChoices[0] : null);
  const switcherSection = sections.find((s) => s.view === activeSectionView)?.label ?? null;
  const pageBar = usePageBottomBarConfig();
  const overlayCount = useOpenOverlayCount();
  const isPhone = usePhoneLayout();
  // The bar's tabs come from one pure derivation (src/lib/headerBottomBar) so
  // they can be tested with the real predicates: surface access AND the global
  // destination gates (Admin is admin-only), and a program bar only for a caller
  // who holds the program.
  const bottomBar = deriveHeaderBottomBar({
    authState: authState as HeaderAuth,
    view: view ?? null,
    showForm,
    showBreedForm,
    showFarrowForm,
    overlayOpen: showMenu || notifOpen || overlayCount > 0,
    pageBar,
  });
  const onBottomTab = (tab: BottomTab) => {
    if (!bottomBar.hidden && bottomBar.kind === 'page' && pageBar && !pageBar.hidden) pageBar.onSelect(tab.key);
    else go(tab.view);
  };
  // CP0 Tabs: the selected sub-nav tab is a filled pill in the PROGRAM color
  // (the header sub-nav adopts program identity); unselected tabs are plain
  // text with no border/box. The dark top-bar chrome stays green (above).
  const navAccent = navProgram ? getProgramColor(navProgram) : '#187A54';
  const nb = (active: boolean): CSSProperties => ({
    padding: '7px 16px',
    borderRadius: 999,
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontSize: 12,
    fontWeight: active ? 700 : 500,
    whiteSpace: 'nowrap',
    border: 'none',
    background: active ? navAccent : 'transparent',
    color: active ? getReadableText(navAccent) : 'var(--text-primary)',
  });
  // Common navigation reset: every header-driven setView must also close
  // any open broiler/pig form drawers so they don't leak into the next
  // view. Extracted here so the hamburger menu's eight destinations don't
  // each have to repeat the three setShow*(false) calls.
  function go(nextView: string) {
    void requestGuardedNavigation(() => {
      setShowForm(false);
      setShowBreedForm(false);
      setShowFarrowForm(false);
      // Re-selecting Processing must leave Labels, but preserve an already-open
      // schedule drawer and its URL. Use the same section precedence as Processing.
      const leavingLabels =
        view === 'processing' &&
        new URLSearchParams(headerLocation.search).get('tab') === 'labels' &&
        !readProcessingDeepLink(headerLocation.search);
      if (nextView === 'processing' && leavingLabels) navigateToProcessingRoute(headerNavigate, '/processing');
      else setView(nextView);
      setShowMenu(false);
    });
  }

  function chooseRolePreview(nextRole: string) {
    void requestGuardedNavigation(() => {
      if (nextRole) setRolePreview(nextRole);
      else clearRolePreview();
      setShowForm(false);
      setShowBreedForm(false);
      setShowFarrowForm(false);
      setView('home');
      setShowMenu(false);
    });
  }

  // Lane 1 CP1: Light users are contained to the field portal. Their menu shows
  // only Home, Dailys, Equipment, and Sign Out (Tasks stays on the header icon).
  // Activity is hidden; the Admin section is already isAdmin-gated below.
  // (isLight/isAdmin are declared with the route-derived nav state above.)
  // Hoisted only so the label lookup is indexed by a `string` rather than by
  // `string | undefined`. Same three-step fallback, same result for every
  // input: a known role maps to its label, an unknown one falls through to the
  // raw role, and an absent one to 'Unknown'.
  const realRole = realAuthState?.role;
  const realRoleLabel = (realRole && ROLE_PREVIEW_LABELS[realRole]) || realRole || 'Unknown';
  const previewRoleLabel = ROLE_PREVIEW_LABELS[rolePreview] || rolePreview;

  // After any view change that keeps us in a sub-nav section, reveal the active
  // tab. scrollActiveIntoStrip writes ONLY the strip's scrollLeft: the pre-lane
  // scrollIntoView() scrolled every scrollable ancestor including the document,
  // which jumped the page body vertically on phones. It also makes the minimum
  // adjustment and no-ops when the tab already fits, so a mid-swipe scroll
  // position is never yanked back.
  React.useEffect(() => {
    const strip = subnavRef.current;
    if (!strip) return;
    scrollActiveIntoStrip(strip, strip.querySelector('[data-subnav-active="1"]'));
  }, [view, activeSectionView]);

  return (
    <div className="no-print">
      {/* ── Dark top bar ── */}
      <div data-header-bar="1" style={S.header}>
        <a
          data-header-brand="1"
          href="/"
          onClick={(e) => {
            e.preventDefault();
            go('home');
          }}
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            padding: 0,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            textDecoration: 'none',
          }}
        >
          <div style={{fontSize: 18, fontWeight: 700, letterSpacing: '-.4px', color: 'white'}}>WCF Planner</div>
        </a>
        {/* ── Global program strip ── the nine destinations, permission-filtered.
            The wordmark above stays the Home link at every breakpoint, so Home
            needs no chip of its own and its semantics are unchanged. */}
        {/* Phones (< 640px): 'Cattle ▾' + the section name replace the wordmark
            and the program strip (Q18); the bottom bar replaces the section row. */}
        {isPhone && navigationReady && (
          <div data-header-phone-switcher="1">
            <ProgramSwitcher
              current={switcherCurrent}
              sectionLabel={switcherSection}
              programs={switcherChoices}
              onSelect={(choice) => go(choice.view)}
            />
          </div>
        )}
        {!isPhone && (
          <GlobalNavStrip
            destinations={globalDestinations}
            activeKey={activeNavKey}
            onSelect={(destination) => go(destination.view)}
          />
        )}
        {/* Save status + identity. `data-header-has-status` is what makes the
            mobile layout deterministic: at phone widths index.html gives this
            container its OWN full-width row when a status is present (so no
            status message, however long, can shorten the brand or push Tasks /
            Notifications / Hamburger off row 1) and hides it entirely when it
            is not (so the hidden identity pill leaves no empty row behind).
            Desktop layout is unchanged. */}
        <div
          data-header-userinfo="1"
          data-header-has-status={hasSaveStatus ? '1' : undefined}
          style={{display: 'flex', alignItems: 'center', gap: 8, marginLeft: 'auto'}}
        >
          {hasSaveStatus && (
            <span data-header-save-status={saveHook} style={{display: 'inline-flex'}}>
              <SaveStatusChipView status={saveSnapshot} />
            </span>
          )}
          {!hasSaveStatus && authState?.name && (
            <div data-header-user-pill="1" style={USER_PILL}>
              <span style={USER_AVATAR}>{(authState.name || '?').trim().charAt(0).toUpperCase() || '?'}</span>
              <span data-header-username="1" style={USER_TXT}>
                <span>{authState.name}</span>
                <span
                  style={{fontSize: 11, fontWeight: 600, color: 'rgba(255,255,255,.82)', textTransform: 'capitalize'}}
                >
                  {ROLE_PREVIEW_LABELS[authState.role] || authState.role}
                </span>
              </span>
            </div>
          )}
        </div>
        {/* Right-side icon group: Tasks, Notifications placeholder, Hamburger.
            Each is a 36x36 white-on-green button with a translucent border.
            On mobile the index.html @media rule reorders them via flex order
            so the brand stays first and these icons stay visible without
            wrapping under the brand. A deactivated account (migration 218)
            gets none of it: every control here opens a surface or a read the
            server refuses it. */}
        {!inactiveAccount && (
          <div
            data-header-action-group="1"
            style={{display: 'flex', gap: 6, alignItems: 'center', marginLeft: 8, position: 'relative'}}
          >
            {/* Compact weather summary + nav button — the sole entry point to the
              dedicated /weather page (Home no longer renders a weather card).
              Routed through go() so any open broiler/pig form drawer closes
              the same way every other header destination does. */}
            {canOpenHeaderView('weather') && <HeaderWeatherChip onOpen={() => go('weather')} />}
            {canOpenHeaderView('tasks') && (
              <button
                data-tasks-header-link="1"
                aria-label={`Tasks${myDueCount > 0 ? ` (${myDueCount} due or overdue)` : ''}`}
                title="Tasks"
                onClick={() => go('tasks')}
                style={view === 'tasks' ? HEADER_ICON_BTN_ACTIVE : HEADER_ICON_BTN}
              >
                <CheckmarkIcon size={18} />
                {myDueCount > 0 && (
                  <span data-tasks-header-badge={myDueCount} style={HEADER_BADGE}>
                    {myDueCount}
                  </span>
                )}
              </button>
            )}
            {/* Notifications Center bell — real, data-backed. Badge shows
              unread count from public.notifications (mig 057). Click
              toggles a dropdown panel with the 20 most recent
              notifications (read + unread). Row click goes to /tasks
              and marks the notification read. "Mark all read" clears
              every unread row for the recipient. */}
            {NOTIFICATIONS_CENTER_ENABLED && navigationReady && (
              <div style={{position: 'relative'}}>
                <button
                  ref={notifBtnRef}
                  data-notifications-header-link="1"
                  aria-label={`Notifications${notifUnread > 0 ? ` (${notifUnread} unread)` : ''}`}
                  title="Notifications"
                  aria-expanded={notifOpen ? 'true' : 'false'}
                  onClick={() => setNotifOpen((o) => !o)}
                  style={notifOpen ? HEADER_ICON_BTN_ACTIVE : HEADER_ICON_BTN}
                >
                  <BellIcon size={18} />
                  {notifUnread > 0 && (
                    <span data-notifications-unread-badge={notifUnread} style={HEADER_BADGE}>
                      {notifUnread}
                    </span>
                  )}
                </button>
                {notifOpen &&
                  createPortal(
                    <div
                      data-notifications-panel-scrim="1"
                      onClick={() => setNotifOpen(false)}
                      style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: HEADER_OVERLAY_Z}}
                    />,
                    document.body,
                  )}
                {notifOpen &&
                  createPortal(
                    <div
                      data-notifications-panel="1"
                      data-notifications-panel-loaded={!notifLoading && !notifLoadError ? '1' : '0'}
                      style={{
                        ...headerAnchorStyle(notifBtnRef),
                        background: 'white',
                        border: '1px solid #E6E8EB',
                        borderRadius: 10,
                        boxShadow: '0 8px 24px rgba(0,0,0,.15)',
                        zIndex: HEADER_OVERLAY_Z + 1,
                        minWidth: 320,
                        maxWidth: 360,
                        maxHeight: 480,
                        overflow: 'hidden',
                        fontFamily: 'inherit',
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <div
                        data-notifications-panel-header="1"
                        style={{
                          padding: '10px 14px',
                          borderBottom: '1px solid #F1F3F4',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                        }}
                      >
                        <span style={{fontSize: 13, fontWeight: 700, color: 'var(--text-primary)'}}>Notifications</span>
                        <span style={{fontSize: 11, color: '#68707A', fontVariantNumeric: 'tabular-nums'}}>
                          {notifUnread > 0 ? `${notifUnread} unread` : 'all read'}
                        </span>
                        <button
                          data-notifications-mark-all-read="1"
                          onClick={async () => {
                            try {
                              await markAllNotificationsRead(sb, callerProfileId);
                            } catch (_e) {
                              /* soft-fail; next refresh will reconcile */
                            }
                          }}
                          disabled={notifUnread === 0 || !!notifLoadError}
                          style={{
                            marginLeft: 'auto',
                            padding: '4px 8px',
                            background: 'none',
                            border: 'none',
                            color: notifUnread === 0 || notifLoadError ? '#68707A' : '#187A54',
                            cursor: notifUnread === 0 || notifLoadError ? 'default' : 'pointer',
                            fontSize: 12,
                            fontWeight: 600,
                            fontFamily: 'inherit',
                          }}
                        >
                          Mark all read
                        </button>
                      </div>
                      <div data-notifications-panel-list="1" style={{overflowY: 'auto', maxHeight: 420}}>
                        {notifLoadError && (
                          <div data-notifications-load-error="1" style={{padding: '12px 14px'}}>
                            <InlineNotice notice={notifLoadError} />
                            <button
                              type="button"
                              data-notifications-retry="1"
                              onClick={() => void retryNotifications()}
                              style={{
                                padding: '6px 12px',
                                borderRadius: 10,
                                border: '1px solid #b91c1c',
                                background: '#b91c1c',
                                color: 'white',
                                cursor: 'pointer',
                                fontSize: 12,
                                fontWeight: 600,
                                fontFamily: 'inherit',
                              }}
                            >
                              Retry
                            </button>
                          </div>
                        )}
                        {!notifLoadError && notifLoading && notifRecent.length === 0 && (
                          <div
                            data-notifications-loading="1"
                            style={{padding: '20px 14px', textAlign: 'center', color: '#68707A', fontSize: 13}}
                          >
                            Loading notifications...
                          </div>
                        )}
                        {!notifLoadError && !notifLoading && notifRecent.length === 0 && (
                          <div
                            data-notifications-empty="1"
                            style={{padding: '20px 14px', textAlign: 'center', color: '#68707A', fontSize: 13}}
                          >
                            No notifications yet.
                          </div>
                        )}
                        {!notifLoadError &&
                          notifRecent.map((n) => {
                            const unread = !n.read_at;
                            return (
                              <button
                                key={n.id}
                                data-notifications-row={n.id}
                                data-notifications-row-unread={unread ? '1' : '0'}
                                onClick={async () => {
                                  // Close the panel first so the navigation doesn't
                                  // leave a stale dropdown floating over /tasks.
                                  setNotifOpen(false);
                                  try {
                                    if (unread) await markNotificationRead(sb, n.id);
                                  } catch (_e) {
                                    /* soft-fail */
                                  }
                                  const route = resolveNotificationRoute(
                                    n,
                                    n.activity_entity_type,
                                    n.activity_entity_id,
                                  );
                                  if (route.startsWith('/processing')) {
                                    // Processing deep links (/processing?record=<id>)
                                    // must keep their query string. go('processing')
                                    // can't — the view→URL adapter writes the bare
                                    // VIEW_TO_PATH path — and raw history.pushState
                                    // desyncs react-router. react-router
                                    // navigation works end-to-end: App's URL
                                    // adapter is keyed on location.pathname, so the
                                    // pathname change flips view='processing' while
                                    // ?record= survives for the view to read on load.
                                    // For the already-mounted-view case (pathname
                                    // unchanged → no remount) the helper also
                                    // dispatches 'wcf-processing-open-record'.
                                    navigateToProcessingRoute(headerNavigate, route);
                                    return;
                                  }
                                  const isRecordPageRoute =
                                    n.type === 'comment_mention' ||
                                    route.startsWith('/tasks/') ||
                                    route.startsWith('/fleet/') ||
                                    route.startsWith('/cattle/herds/') ||
                                    route.startsWith('/cattle/batches/') ||
                                    route.startsWith('/sheep/flocks/') ||
                                    route.startsWith('/sheep/batches/') ||
                                    route.startsWith('/layer/batches/') ||
                                    route.startsWith('/layer/housings/') ||
                                    route.startsWith('/broiler/batches/') ||
                                    route.startsWith('/pig/batches/') ||
                                    route.startsWith('/broiler/dailys/') ||
                                    route.startsWith('/layer/dailys/') ||
                                    route.startsWith('/layer/eggs/') ||
                                    route.startsWith('/pig/dailys/') ||
                                    route.startsWith('/cattle/dailys/') ||
                                    route.startsWith('/sheep/dailys/') ||
                                    route.startsWith('/weigh-in-sessions/');
                                  if (isRecordPageRoute && route.startsWith('/')) {
                                    headerNavigate(route);
                                    setNotifOpen(false);
                                    return;
                                  }
                                  const {view: targetView} = routeToView(route);
                                  go(targetView);
                                }}
                                style={{
                                  display: 'block',
                                  width: '100%',
                                  textAlign: 'left',
                                  background: unread ? '#ecfdf5' : 'white',
                                  border: 'none',
                                  borderBottom: '1px solid #F1F3F4',
                                  padding: '10px 14px',
                                  cursor: 'pointer',
                                  fontFamily: 'inherit',
                                }}
                              >
                                <div
                                  style={{
                                    fontSize: 13,
                                    fontWeight: unread ? 700 : 500,
                                    color: 'var(--text-primary)',
                                    lineHeight: 1.35,
                                  }}
                                >
                                  {n.title}
                                </div>
                                {n.body && (
                                  <div
                                    style={{
                                      fontSize: 12,
                                      color: '#5B636D',
                                      marginTop: 3,
                                      lineHeight: 1.4,
                                      whiteSpace: 'pre-wrap',
                                      wordBreak: 'break-word',
                                    }}
                                  >
                                    {n.body}
                                  </div>
                                )}
                                <div
                                  style={{
                                    fontSize: 11,
                                    color: '#68707A',
                                    marginTop: 4,
                                    fontVariantNumeric: 'tabular-nums',
                                  }}
                                >
                                  {new Date(n.created_at).toLocaleString()}
                                </div>
                              </button>
                            );
                          })}
                      </div>
                    </div>,
                    document.body,
                  )}
              </div>
            )}
            {authState?.user && (
              <div style={{position: 'relative'}}>
                <button
                  ref={menuBtnRef}
                  data-header-menu-toggle="1"
                  aria-label="Menu"
                  aria-expanded={showMenu ? 'true' : 'false'}
                  onClick={() => setShowMenu((m) => !m)}
                  style={showMenu ? HEADER_ICON_BTN_ACTIVE : HEADER_ICON_BTN}
                >
                  <MenuIcon size={18} />
                </button>
                {showMenu &&
                  createPortal(
                    <div
                      onClick={() => setShowMenu(false)}
                      style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: HEADER_OVERLAY_Z}}
                    />,
                    document.body,
                  )}
                {showMenu &&
                  createPortal(
                    <div
                      data-header-menu="1"
                      style={{
                        ...headerAnchorStyle(menuBtnRef),
                        background: 'white',
                        border: '1px solid #E6E8EB',
                        borderRadius: 10,
                        boxShadow: '0 8px 24px rgba(0,0,0,.15)',
                        zIndex: HEADER_OVERLAY_Z + 1,
                        minWidth: 220,
                        overflow: 'hidden',
                        fontFamily: 'inherit',
                      }}
                    >
                      {canOpenHeaderView('home') && (
                        <button data-header-menu-item="home" onClick={() => go('home')} style={MENU_ITEM_BTN}>
                          <HomeGlyph size={16} /> Home
                        </button>
                      )}
                      {canOpenHeaderView('activity') && (
                        <button data-header-menu-item="activity" onClick={() => go('activity')} style={MENU_ITEM_BTN}>
                          <ActivityGlyph size={16} /> Activity
                        </button>
                      )}
                      {/* Processing is an operational destination, not a Webform
                        (Ronnie, 2026-10-20): above the Webforms divider, and only
                        on phones, where the bar gives way to the switcher. On
                        desktop its chip is always on the bar. Gated by the SAME
                        operational-role predicate as the chip and as App's
                        /processing bounce, so this never widens who sees it. */}
                      {isPhone && canOpenHeaderView('processing') && (
                        <button
                          data-header-menu-item="processing"
                          onClick={() => go('processing')}
                          style={MENU_ITEM_BTN}
                        >
                          <PlannerIcon iconKey="processing" size={16} /> Processing
                        </button>
                      )}

                      {(offersDailys || canOpenHeaderView('fuelingHub')) && (
                        <>
                          <div style={MENU_DIVIDER} />
                          <span style={MENU_SECTION_LABEL}>Webforms</span>
                        </>
                      )}
                      {/* Owner answer E (2026-10-09): Daily Reports are outside
                        Equipment Tech's menu; access.ts refuses /dailys too. */}
                      {offersDailys && (
                        <button data-header-menu-item="dailys" onClick={() => go('webformhub')} style={MENU_ITEM_BTN}>
                          <PlannerIcon iconKey="daily-reports" size={16} /> Dailys
                        </button>
                      )}
                      {canOpenHeaderView('fuelingHub') && (
                        <button
                          data-header-menu-item="equipment"
                          onClick={() => go('fuelingHub')}
                          style={MENU_ITEM_BTN}
                        >
                          <PlannerIcon iconKey="tractor" size={16} /> Equipment
                        </button>
                      )}

                      {isAdmin && canOpenHeaderView('webforms') && (
                        <>
                          <div style={MENU_DIVIDER} />
                          <button data-header-menu-item="admin" onClick={() => go('webforms')} style={MENU_ITEM_BTN}>
                            <PlannerIcon iconKey="admin" size={16} /> Admin
                          </button>
                          <button
                            data-header-menu-item="users"
                            onClick={() => {
                              // A41: Users is an Admin tab page now, not a modal.
                              void requestGuardedNavigation(() => {
                                headerNavigate('/admin?tab=users');
                                setShowMenu(false);
                              });
                            }}
                            style={MENU_ITEM_BTN}
                          >
                            <UsersGlyph size={16} /> Users
                          </button>
                          {/* Newsletter stays OUT of the nine bar destinations by
                            design and remains reachable here, unchanged. */}
                          <button
                            data-header-menu-item="newsletter"
                            onClick={() => go('newsletterAdmin')}
                            style={MENU_ITEM_BTN}
                          >
                            <NewsletterGlyph size={16} /> Newsletter
                          </button>
                        </>
                      )}

                      {canUseRolePreview && (
                        <>
                          <div style={MENU_DIVIDER} />
                          <span style={MENU_SECTION_LABEL}>Role Preview</span>
                          <div data-role-preview-menu="1" style={{padding: '8px 16px 12px'}}>
                            <select
                              data-role-preview-select="1"
                              value={rolePreview || ''}
                              onChange={(e) => chooseRolePreview(e.target.value)}
                              style={ROLE_PREVIEW_SELECT}
                            >
                              <option value="">Actual role ({realRoleLabel})</option>
                              {(rolePreviewRoles || []).map((role) => (
                                <option key={role} value={role}>
                                  {ROLE_PREVIEW_LABELS[role] || role}
                                </option>
                              ))}
                            </select>
                          </div>
                        </>
                      )}

                      <div style={MENU_DIVIDER} />
                      <button
                        data-header-menu-item="sign-out"
                        onClick={() => {
                          setShowMenu(false);
                          signOut();
                        }}
                        style={{...MENU_ITEM_BTN, color: '#b91c1c'}}
                      >
                        <SignOutGlyph size={16} /> Sign Out
                      </button>
                    </div>,
                    document.body,
                  )}
              </div>
            )}
          </div>
        )}
      </div>
      {/* PROFILE LOAD FAILED — say so, rather than presenting a degraded app.

          `loadUserImpl` resolves the profile within ONE overall 20-second budget
          — a genuine timeout ends it outright, and only clearly transient
          failures retry inside what remains — and then fails CLOSED: role
          becomes 'inactive', the view is forced to
          home, and the inactive recovery shell renders this Header without a
          route body. That security posture is correct — a failed profile must
          never elevate a role — but until now its only other output was a
          console.error. The result on a transient network blip was an app that
          looked like a permissions problem:
          Processing/Admin gone, every count zero, no explanation, and no way
          back except knowing to hard-reload. Reported from production 2026-08-14.

          Retry reloads rather than re-entering the boot coordinator: this state
          means the session never finished opening, so there is no in-flight
          work to preserve, and a reload re-establishes it exactly the way the
          manual recovery already did. */}
      {/* An emailed mention link whose lookup could not be completed. This is
          ONLY shown for a transport failure that exhausted its retries -- a
          notification that simply is not this caller's resolves successfully
          and stays silent, so this banner can never tell anyone whether a
          notification exists. Without it the link failed invisibly: the
          operator landed on the home page with nothing to act on, which is
          exactly how this defect stayed hidden through three test sends. */}
      {mentionLinkFailed && (
        <div
          data-mention-link-failed-banner="1"
          role="alert"
          style={{
            background: '#fffbeb',
            borderBottom: '1px solid #fde68a',
            color: '#78350f',
            padding: '8px 1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <span>We couldn&apos;t open that link. Check your connection and try again.</span>
          <button
            type="button"
            data-mention-link-retry="1"
            onClick={() => {
              const retryId = readMentionLinkId();
              if (retryId && callerProfileId) mentionLinkState.delete(`${callerProfileId}:${retryId}`);
              setMentionLinkFailed(false);
              setMentionLinkRetryTick((n) => n + 1);
            }}
            style={{
              border: '1px solid #d97706',
              background: 'white',
              color: '#92400e',
              // Pill, matching the profile-recovery banner's Retry beside it.
              // The radius floor guard (CP0 A3) rejects sub-10px radii.
              borderRadius: 999,
              padding: '5px 10px',
              fontSize: 12,
              fontWeight: 700,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Retry
          </button>
        </div>
      )}
      {profileStatus === PROFILE_STATUS.FAILED && (
        <div
          data-profile-load-failed-banner="1"
          role="alert"
          style={{
            background: '#fef2f2',
            borderBottom: '1px solid #fecaca',
            color: '#7f1d1d',
            padding: '8px 1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          {/* Wording is deliberately neutral about the DURABLE grant. The
              previous copy promised "Your permissions have not changed", which
              is a claim this state cannot support: the profile read is exactly
              what failed, so the only honest thing the client knows is that it
              could not verify anything. Saying otherwise also reads as a
              reassurance in the one case where it would be false — a genuine
              revocation or deactivation lands in this same fail-closed shell
              and would be actively contradicted by the banner. Access here IS
              reduced, to least-privilege inactive; "temporarily limited" states
              the effect without asserting a cause. */}
          <span>We couldn&apos;t verify your profile, so access is temporarily limited.</span>
          <button
            type="button"
            data-profile-load-retry="1"
            onClick={() => window.location.reload()}
            style={{
              marginLeft: 'auto',
              padding: '5px 10px',
              borderRadius: 999,
              border: '1px solid #fecaca',
              background: '#fff',
              color: '#7f1d1d',
              fontWeight: 700,
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            Retry
          </button>
        </div>
      )}
      {canUseRolePreview && rolePreview && (
        <div
          data-role-preview-banner="1"
          style={{
            background: '#fffbeb',
            borderBottom: '1px solid #fde68a',
            color: '#78350f',
            padding: '8px 1.25rem',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
            fontSize: 12,
            fontWeight: 600,
          }}
        >
          <span>
            Previewing {previewRoleLabel}. Server permissions remain {realRoleLabel}.
          </span>
          <select
            data-role-preview-banner-select="1"
            value={rolePreview || ''}
            onChange={(e) => chooseRolePreview(e.target.value)}
            style={{...ROLE_PREVIEW_SELECT, width: 180, padding: '5px 8px'}}
          >
            <option value="">Actual role ({realRoleLabel})</option>
            {(rolePreviewRoles || []).map((role) => (
              <option key={role} value={role}>
                {ROLE_PREVIEW_LABELS[role] || role}
              </option>
            ))}
          </select>
          <button
            type="button"
            data-role-preview-exit="1"
            onClick={() => chooseRolePreview('')}
            style={{
              marginLeft: 'auto',
              padding: '5px 10px',
              borderRadius: 999,
              border: '1px solid #f59e0b',
              background: 'white',
              color: '#78350f',
              cursor: 'pointer',
              fontSize: 12,
              fontWeight: 700,
              fontFamily: 'inherit',
            }}
          >
            Exit preview
          </button>
        </div>
      )}
      {/* ── Light sub-nav bar — only in section views with tabs ── */}
      {/* Wrapper carries position:relative so the right-edge chevron hint
          in index.html can anchor to the viewport edge instead of the
          scroller's content edge. */}
      {inSubnav && !isPhone && (
        <div data-header-subnav-wrap="1" style={{position: 'relative'}}>
          <div
            ref={subnavRef}
            data-header-subnav="1"
            style={{
              background: 'white',
              borderBottom: '1px solid #E6E8EB',
              padding: '8px 1.25rem',
              display: 'flex',
              gap: 6,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            {/* Row 2 is the CURRENT program's sections only. The Home button
                and its divider are retired: the wordmark one row up is the
                Home link at every breakpoint, so repeating it here spent a
                target on a destination that never left the screen. */}
            {sections.map((s) => {
              const active = s.view === activeSectionView;
              return (
                <button
                  key={s.view}
                  data-subnav-item={s.view}
                  data-subnav-active={active ? '1' : undefined}
                  aria-current={active ? 'page' : undefined}
                  style={nb(active)}
                  onClick={() => {
                    void requestGuardedNavigation(() => {
                      // Clearing the three drawers is what the pre-lane per-program
                      // handlers each did by hand; doing it unconditionally is
                      // equivalent and keeps one code path.
                      setShowForm(false);
                      setShowBreedForm(false);
                      setShowFarrowForm(false);
                      setView(s.view);
                    });
                  }}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
      {isPhone && navigationReady && <BottomBar model={bottomBar} onSelect={onBottomTab} />}
    </div>
  );
}
